import { rows, charts, Grid, Widget, DataGrid } from "colbi_web_ui/lib/components";
import { parseFilter } from "colbi_web_ui/lib/utils";
import App from "../controllers/App/App";
import { checkForCustomFragment } from "../utils/queryUtils";
import { REACT_APP_DEFAULT_PAGE_SIZE } from "../environmentVariables";
import GenericViewsToolbar from "../components/GenericViewsToolbar/GenericViewsToolbar";

const queries = (args = {}) => {
    const entityResource = args.modulePrefix ? `${args.modulePrefix}_${args.entity}` : args.entity;
    const result = [];

    const customEntityFragment = checkForCustomFragment(args.modulePrefix, args.entity);
    if (customEntityFragment) {
        result.push(
            {
                resource: entityResource,
                args: {
                    projectId: args.projectId,
                    fiscalYear: args.fiscalYear,
                    modulePrefix: args.modulePrefix,
                    importId: args.importId,
                    filter: args.filter
                        ? { type: "[FilterInput!]", value: parseFilter(args.filter) }
                        : undefined,
                    sort: args.sort
                        ? { type: "[SortInput!]", value: parseFilter(args.sort) }
                        : undefined,
                    page: parseInt(args.page, 10) || 0,
                },
                body: `__typename, items{ ...${entityResource} }, hasMore, total, totalizers, filters(projectId:$projectId,fiscalYear:$fiscalYear,modulePrefix:$modulePrefix){ prop, name, type, list, disableOperators, mandatory }`,
            },
            {
                resource: `${entityResource}_summary`,
                args: {
                    projectId: args.projectId,
                    fiscalYear: args.fiscalYear,
                    modulePrefix: args.modulePrefix,
                },
                body: "summary{ key, value }",
            },
            {
                resource: `${entityResource}_export`,
                args: {
                    projectId: args.projectId,
                    fiscalYear: args.fiscalYear,
                    modulePrefix: args.modulePrefix,
                    filter: args.filter
                        ? { type: "[FilterInput!]", value: parseFilter(args.filter) }
                        : undefined,
                    sort: args.sort
                        ? { type: "[SortInput!]", value: parseFilter(args.sort) }
                        : undefined,
                    page: parseInt(args.page, 10) || 0,
                },
                body: `items{...${entityResource}}`,
                downloadable: true,
                downloadOnly: true,
                xlsxTranslation: entityResource,
            },
        );
    } else {
        result.push(
            {
                resource: "generic_views",
                args: {
                    projectId: args.projectId,
                    fiscalYear: args.fiscalYear,
                    importId: args.importId,
                    modulePrefix: args.modulePrefix,
                    entity: args.entity,
                    filter: args.filter
                        ? { type: "[FilterInput!]", value: parseFilter(args.filter) }
                        : undefined,
                    sort: args.sort
                        ? { type: "[SortInput!]", value: parseFilter(args.sort) }
                        : undefined,
                    page: parseInt(args.page, 10) || 0,
                    pageSize: parseInt(args.pageSize || REACT_APP_DEFAULT_PAGE_SIZE),
                },
                body: `__typename, items, headerItems, hasMore, total, totalizers, entity, exportableEntities, filters(projectId:$projectId,fiscalYear:$fiscalYear,modulePrefix:$modulePrefix,entity:$entity,importId:$importId){ prop, name, type, list, disableOperators, mandatory, mask}`,
                xlsxTranslation: args.entity,
                isDatagridReq: true,
            },
            {
                resource: `generic_view_summary`,
                args: {
                    projectId: args.projectId,
                    fiscalYear: args.fiscalYear,
                    modulePrefix: args.modulePrefix,
                    importId: args.importId,
                    entity: args.entity,
                },
                body: "options, summary, setClose, graphicName",
            },
            {
                resource: "generic_views_export",
                args: {
                    locale: args.locale || "en",
                    projectId: args.projectId,
                    fiscalYear: args.fiscalYear,
                    importId: args.importId,
                    modulePrefix: args.modulePrefix,
                    entity: args.entity,
                    selectedExportableOption: undefined,
                    filter: args.filter
                        ? { type: "[FilterInput!]", value: parseFilter(args.filter) }
                        : undefined,
                    sort: args.sort
                        ? { type: "[SortInput!]", value: parseFilter(args.sort) }
                        : undefined,
                    page: parseInt(args.page, 10) || 0,
                },
                body: `__typename, items, headerItems, hasMore, total, totalizers, entity, xlsxLabels, filters(projectId:$projectId,fiscalYear:$fiscalYear,modulePrefix:$modulePrefix,entity:$entity,importId:$importId){ prop, name, type, list, disableOperators, mandatory }`,
                downloadable: true,
                downloadOnly: true,
                xlsxTranslation: args.entity,
            },
        );
    }
    return result.filter((q) => !!q.resource);
};

const transactionsByFile = (path, navigation, exact, navByImportId = false) => ({
    path,
    navigation,
    component: App,
    queries,
    exact,
    navByImportId,
    title: ["label(param(entity))"].filter((l) => l),
    components: [
        {
            component: Widget,
            modifiers: ["--root-margin-bottom-small"],
            props: {
                setClose: "query(1).setClose",
                data: "query(1).list",
                icon: "sales",
                title: "label(query(1).graphicName)",
                entity: "param(entity)",
                children: [
                    {
                        component: Grid,
                        props: {
                            columnsTemplate: [1],
                            aspect: ["18x6"],
                            items: [
                                {
                                    data: "query(1).summary",
                                    labelsPosition: "inside",
                                    stacked: true,
                                    summarized: true,
                                    links: [
                                        {
                                            to: [
                                                "param(projectId)",
                                                "param(fiscalYear)",
                                                "param(importId)",
                                                "param(modulePrefix)",
                                                "/",
                                                "param(entity)",
                                            ],
                                            filterColumn: "Period",
                                            options: "query(1).options",
                                        },
                                    ],
                                },
                            ],
                            cards: [charts.BarChart],
                        },
                    },
                ],
            },
        },
        {
            component: DataGrid,
            props: {
                id: "results",
                card: rows.CellCard,
                items: "query(0).list",
                header: "query(0).header",
                footer: "query(0).footer",
                metadata: "query(0).metadata",
                page: "param(page)",
                status: "status(0)",
                sort: "param(sort)",
                pageSize: "param(pageSize)",
                pageDefault: 10,
                filter: "param(filter)",
                availableFilters: "query(0).filters",
                wrapper: "wrapper",
                showCheckbox: "query(0).showCheckbox",
                total: "query(0).metadata.total",
                hideDeleteSeriesButton: true,
                basePath: path,
                totalizers: "query(0).totalizers",
                toolbar: GenericViewsToolbar,
                toolbarProps: {
                    data: "query(0).list",
                    exportableArray: "query(0).exportableEntities",
                },
            },
        },
    ],
});

export default transactionsByFile;
